/**
 * useImpactConfirm — the confirm dialog shown before every sequence action that changes who gets
 * emails or when: turning on/off, saving, deleting. It asks the server what the action WILL do
 * (POST /api/email/sequences/impact — a dry run, writes nothing) and shows it with real numbers.
 *
 *   const [confirmImpact, impactDialog] = useImpactConfirm({ getImpact: props.getSequenceImpact, showAlert });
 *   const answer = await confirmImpact(
 *     { action: 'activate', sequenceId },                    // impact request
 *     { title, lead?, okText, danger?, skipIfNothingToSay?, alternate?: { text, value }, onLoaded? }
 *   );
 *   if (!answer) return;                       // cancelled (or the impact request failed)
 *   answer.includeExisting                     // the "send also to existing customers" choice
 *   answer.alternate                           // set when the alternate button was clicked (e.g. 'pause')
 *   render {impactDialog} somewhere in the component.
 *
 * The dialog shows: the lines from impactLines() (only those with numbers > 0), warnings
 * (a blocking one disables the confirm button), and — when people would enter now and some
 * customers already have the tag — the choice "only from now on" (default) / "also existing",
 * with per-tag counts, who won't get it and why, and how long the first send will take.
 * skipIfNothingToSay: resolve at once (no dialog) when there are no lines, blocking warnings or choice.
 * onLoaded: called once the impact arrived (e.g. to stop a button spinner before the dialog opens).
 */
import React, { useRef, useState } from 'react';
import { InfoCircleOutlined, WarningOutlined, ExclamationCircleOutlined, StopOutlined } from '@ant-design/icons';
import { Modal, Radio, Button } from '../../../ui';
import { impactLines, impactWarnings, hasBlockingWarning, asksAboutExisting, excludedText } from './sequenceHelpers';
import classes from './SequenceUpdater.css';

const TONE_ICON = {
  info: <InfoCircleOutlined />,
  warn: <ExclamationCircleOutlined />,
  danger: <WarningOutlined />,
};

const useImpactConfirm = ({ getImpact, showAlert }) => {
  const [state, setState] = useState(null); // { impact, options } while the dialog is open
  const [choice, setChoice] = useState('new');
  const resolver = useRef(null);

  const ask = async (request, options = {}) => {
    let impact;
    try {
      impact = await getImpact(request);
    } catch (e) {
      console.error('impact failed:', e);
      if (showAlert) showAlert('לא הצלחנו לבדוק מה הפעולה תעשה — נסו שוב');
      return null;
    } finally {
      if (options.onLoaded) options.onLoaded();
    }
    const lines = impactLines(impact);
    const warnings = impactWarnings(impact);
    const asks = asksAboutExisting(impact);
    if (options.skipIfNothingToSay && !lines.length && !warnings.some((w) => w.blocking) && !asks) {
      return { includeExisting: false, impact };
    }
    setChoice('new');
    setState({ impact, options });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  };

  const close = (answer) => {
    setState(null);
    if (resolver.current) resolver.current(answer);
    resolver.current = null;
  };

  let body = null;
  let blocking = false;
  if (state) {
    const { impact } = state;
    const lines = impactLines(impact);
    const warnings = impactWarnings(impact);
    blocking = hasBlockingWarning(impact);
    const holders = impact.existingTagHolders;
    const perTag = holders ? holders.tags.filter((t) => t.eligible > 0).map((t) => `${t.name || `#${t.tagId}`}: ${t.eligible}`) : [];
    const excluded = holders ? excludedText(holders.excluded) : '';

    body = (
      <div className={classes.existing}>
        {state.options.lead ? <p className={classes.existingText}>{state.options.lead}</p> : null}

        {lines.length ? (
          <ul className={classes.impactLines}>
            {lines.map((l) => (
              <li key={l.key} className={classes[`tone_${l.tone}`]}>
                {TONE_ICON[l.tone]}
                <span>{l.text}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {asksAboutExisting(impact) ? (
          <div className={classes.existingBlock}>
            <p className={classes.existingText}>
              ל-<strong className="ui-tabular">{holders.eligible}</strong> לקוחות כבר יש{' '}
              {holders.appliesTo === 'added_tags' ? 'את הטאג שהוספתם' : 'את הטאג'}. לשלוח גם להם?
            </p>
            <Radio.Group value={choice} onChange={(e) => setChoice(e.target.value)} className={classes.existingChoices}>
              <Radio value="new">
                <span className={classes.choiceTitle}>רק מי שיקבל את הטאג מעכשיו</span>
                <span className={classes.choiceHint}>הלקוחות שכבר יש להם את הטאג לא יקבלו את הרצף</span>
              </Radio>
              <Radio value="all">
                <span className={classes.choiceTitle}>שלח גם ל-{holders.eligible} הלקוחות שכבר יש להם את הטאג</span>
                <span className={classes.choiceHint}>
                  הם ייכנסו לרצף עכשיו ויתחילו מהמייל הראשון
                  {perTag.length > 1 ? ` (${perTag.join(' · ')})` : ''}.
                </span>
                {excluded ? <span className={classes.choiceHint}>{excluded}.</span> : null}
                {holders.estimatedDaysForFirstEmail > 1 ? (
                  <span className={classes.choiceHint}>
                    המייל הראשון לכולם ייקח לפחות {holders.estimatedDaysForFirstEmail} ימים, בגלל מגבלת השליחה היומית.
                  </span>
                ) : null}
              </Radio>
            </Radio.Group>
          </div>
        ) : null}

        {warnings.length ? (
          <ul className={classes.impactWarnings}>
            {warnings.map((w) => (
              <li key={w.code} className={w.blocking ? classes.tone_danger : classes.tone_warn}>
                {w.blocking ? <StopOutlined /> : <ExclamationCircleOutlined />}
                <span>{w.text}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {!lines.length && !warnings.length && !asksAboutExisting(impact) ? (
          <p className={classes.existingText}>אין לקוחות שיושפעו מהפעולה הזו.</p>
        ) : null}
      </div>
    );
  }

  const options = (state && state.options) || {};
  const dialog = (
    <Modal
      open={!!state}
      title={options.title}
      onCancel={() => close(null)}
      zIndex={1100}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={() => close(null)}>
          ביטול
        </Button>,
        options.alternate ? (
          <Button key="alternate" onClick={() => close({ includeExisting: false, alternate: options.alternate.value })}>
            {options.alternate.text}
          </Button>
        ) : null,
        <Button
          key="ok"
          type="primary"
          danger={!!options.danger}
          disabled={blocking}
          onClick={() => close({ includeExisting: choice === 'all', impact: state && state.impact })}
        >
          {options.okText || 'המשך'}
        </Button>,
      ]}
    >
      {body}
    </Modal>
  );

  return [ask, dialog];
};

export default useImpactConfirm;
